import { SITE } from "../config";
import { usePageMeta } from "../seo";
import { ArrowRightIcon, CheckIcon, MailIcon } from "../components/Icons";

const topics = [
  "Whether ZopDesk fits how your team works today",
  "Help connecting a mailbox, WhatsApp number, Acefone account or store",
  "Something that's missing, or a bug you ran into",
  "Questions about how your data is handled",
];

const faq = [
  {
    q: "Do I need to talk to anyone before I start?",
    a: "No. Sign in with your Google account and you have a workspace straight away. Writing to us is for when you have a question, not a step you have to go through.",
  },
  {
    q: "Do I need a Shopify store?",
    a: "No. Shopify is one of the integrations. Email, WhatsApp, phone, ticketing, rules, saved replies and reports work without it.",
  },
  {
    q: "Can you move our tickets over from another helpdesk?",
    a: "There's no importer yet. New conversations start in ZopDesk from the moment you connect a channel. If importing history matters to you, tell us; it helps us decide what to build next.",
  },
];

export default function Contact() {
  usePageMeta("/contact");
  return (
    <>
      <section className="mx-auto max-w-6xl px-4 pt-16 pb-12 sm:px-6 sm:py-20">
        <div className="grid gap-10 lg:grid-cols-2">
          <div>
            <p className="mb-3 animate-fade-up text-xs font-semibold uppercase tracking-wider text-brand-700">
              Talk to us
            </p>
            <h1
              className="animate-fade-up text-4xl font-semibold tracking-tight text-ink-900 sm:text-5xl"
              style={{ animationDelay: "60ms" }}
            >
              Ask us anything.
            </h1>
            <p
              className="mt-4 animate-fade-up text-lg text-ink-600"
              style={{ animationDelay: "140ms" }}
            >
              ZopDesk is in beta and built by a small team. Email is the best
              way to reach us. Good things to write about:
            </p>
            <ul
              className="mt-8 animate-fade-up space-y-3"
              style={{ animationDelay: "220ms" }}
            >
              {topics.map((p) => (
                <li key={p} className="flex items-start gap-3 text-ink-700">
                  <span
                    aria-hidden
                    className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-brand-50 text-brand-700 ring-1 ring-brand-100"
                  >
                    <CheckIcon width={12} height={12} strokeWidth={2.4} />
                  </span>
                  {p}
                </li>
              ))}
            </ul>
          </div>

          <div className="flex flex-col justify-center rounded-2xl border border-ink-200 bg-white p-6 shadow-sm sm:p-8">
            <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-lg bg-brand-50 text-brand-700 ring-1 ring-brand-100">
              <MailIcon width={20} height={20} />
            </div>
            <h2 className="text-xl font-semibold text-ink-900">Email us</h2>
            <p className="mt-2 text-sm text-ink-600">
              Tell us a little about your team and what you're trying to do.
            </p>
            <a
              href={`mailto:${SITE.contactEmail}?subject=ZopDesk`}
              className="mt-5 inline-flex items-center justify-center gap-2 break-all rounded-md bg-gradient-to-br from-brand-500 to-violet-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-brand-500/30 transition hover:from-brand-400 hover:to-violet-400"
            >
              {SITE.contactEmail}
            </a>
            <p className="mt-6 border-t border-ink-100 pt-5 text-sm text-ink-600">
              Want to try it first?{" "}
              <a
                href={SITE.appUrl}
                className="inline-flex items-center gap-1 font-semibold text-brand-700 hover:text-brand-600"
              >
                Start free with Google
                <ArrowRightIcon width={14} height={14} />
              </a>
            </p>
          </div>
        </div>
      </section>

      <section className="border-t border-ink-200 bg-white">
        <div className="mx-auto max-w-3xl px-4 py-20 sm:px-6">
          <h2 className="text-2xl font-semibold tracking-tight text-ink-900 sm:text-3xl">
            A few things people ask.
          </h2>
          <div className="mt-8 space-y-4">
            {faq.map((f) => (
              <div key={f.q} className="rounded-xl border border-ink-200 bg-ink-50 p-5">
                <p className="text-sm font-semibold text-ink-900">{f.q}</p>
                <p className="mt-2 text-sm leading-relaxed text-ink-600">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
